import { useCallback, useSyncExternalStore } from 'react';

export type Theme = 'light' | 'dark' | 'shader' | 'pastel';

/* ── Nuanciers du mode pastel ─────────────────────────────────────────
   Le mode « pastel » est une DA cousine de Visaly (« Cream & Ink »),
   posée sur le bleu Evinly. Les couleurs vivent entièrement dans
   client/index.css (blocs `.pastel[data-palette="…"]`, générés par
   tools/pastelPalettes.mjs) : le code ne connaît que la clé du nuancier,
   écrite en `data-palette` sur la racine.

   Deux familles :
   - « evinly » : bleus Evinly dominants, une seule note de couleur, tons
     sobres ; sidebar et cadre en bleu Evinly, fond bleu ciel très clair.
   - « visaly » : nuancier Visaly (jaune / rose / bleu / kaki) et ses
     rotations sur le cercle chromatique ; sidebar encre. */
export type Palette =
  | 'ciel-rose' | 'ciel-sauge' | 'ocean' | 'terre' | 'ete'
  | 'tetrade' | 'cousine' | 'triade' | 'complementaire' | 'rotation';

/** Panneau de la sidebar (et bordure du cadre) : encre bleu nuit ou bleu Evinly. */
export type Panel = 'ink' | 'blue';

export const PALETTES: { key: Palette; label: string; hint: string; family: 'evinly' | 'visaly'; panel: Panel }[] = [
  { key: 'ciel-rose', label: 'Ciel & rose', hint: 'Bleus Evinly du plus pâle au pervenche, une note rose poudré', family: 'evinly', panel: 'blue' },
  { key: 'ciel-sauge', label: 'Ciel & sauge', hint: 'Mêmes bleus, une note vert sauge — le plus sobre', family: 'evinly', panel: 'blue' },
  { key: 'ocean', label: 'Océan', hint: 'Dégradé tonal marine → cyan → aqua, adouci', family: 'evinly', panel: 'blue' },
  { key: 'terre', label: 'Terre & ciel', hint: 'Sable, bleu ardoise, gris perle, une touche terracotta', family: 'evinly', panel: 'blue' },
  { key: 'ete', label: 'Été', hint: 'Bleu, sarcelle, crème et pêche, une touche corail', family: 'evinly', panel: 'blue' },
  { key: 'tetrade', label: 'Tétrade', hint: 'Carré à 90° construit sur le bleu Evinly — bleu ciel, orchidée, miel, menthe', family: 'visaly', panel: 'ink' },
  { key: 'cousine', label: 'Cousine', hint: 'Nuancier Visaly tel quel — jaune, rose, bleu, kaki', family: 'visaly', panel: 'ink' },
  { key: 'triade', label: 'Triade', hint: 'Triangle à 120° sur le bleu Evinly — bleu ciel, framboise, vert prairie', family: 'visaly', panel: 'ink' },
  { key: 'complementaire', label: 'Complémentaire', hint: 'Nuancier Visaly retourné de 180°, sur fond bleu ciel pâle', family: 'visaly', panel: 'ink' },
  { key: 'rotation', label: 'Rotation +30°', hint: 'Cercle chromatique Visaly tourné de 30° — anis, corail, lavande', family: 'visaly', panel: 'ink' },
];
export const DEFAULT_PALETTE: Palette = 'ete';

/** Panneau par défaut d'un nuancier (celui de sa famille). */
export function defaultPanelFor(palette: Palette): Panel {
  return PALETTES.find((p) => p.key === palette)?.panel ?? 'ink';
}

const THEME_KEY = 'evinly-theme';
const PALETTE_KEY = 'evinly-palette';
const PANEL_KEY = 'evinly-panel';
const THEMES: readonly Theme[] = ['light', 'dark', 'shader', 'pastel'];
const PALETTE_KEYS: readonly Palette[] = PALETTES.map((p) => p.key);

function readTheme(): Theme {
  const stored = localStorage.getItem(THEME_KEY);
  if ((THEMES as readonly string[]).includes(stored ?? '')) return stored as Theme;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function readPalette(): Palette {
  const stored = localStorage.getItem(PALETTE_KEY);
  return (PALETTE_KEYS as readonly string[]).includes(stored ?? '') ? (stored as Palette) : DEFAULT_PALETTE;
}

/** null = panneau par défaut du nuancier ; sinon le choix explicite de l'utilisateur. */
function readPanel(): Panel | null {
  const stored = localStorage.getItem(PANEL_KEY);
  return stored === 'ink' || stored === 'blue' ? stored : null;
}

/* Un seul état pour toute l'application (App ET l'écran de connexion, qui
   n'est pas un descendant de App côté état) : store externe + useSyncExternalStore.
   Les classes de la racine sont posées AVANT le premier rendu, pour que
   l'écran de connexion se peigne directement dans le bon thème. */
type ThemeState = { theme: Theme; palette: Palette; panel: Panel | null };

/* ── Verrou d'interface ───────────────────────────────────────────────
   Décision du 2026-09-10 : la DA « Été » (mode pastel, nuancier `ete`,
   sidebar bleu Evinly) est la seule interface servie, écran de connexion
   compris. Les thèmes clair / sombre / shader et les autres nuanciers
   restent dans le code mais ne sont plus proposés : ce verrou ignore le
   choix mémorisé dans le navigateur et rend les bascules inertes (les
   commandes de ui/ThemeControls.tsx ne se rendent plus). Passer
   THEME_LOCKED à false rouvre le choix, sans rien d'autre à faire. */
export const THEME_LOCKED = true;
const LOCKED_STATE: ThemeState = { theme: 'pastel', palette: 'ete', panel: null };
let state: ThemeState | null = null;
const listeners = new Set<() => void>();

function applyToRoot({ theme, palette, panel }: ThemeState) {
  const root = document.documentElement;
  // dark s'applique aussi en shader (les utilitaires `dark:` y restent actifs).
  root.classList.toggle('dark', theme === 'dark' || theme === 'shader');
  root.classList.toggle('shader', theme === 'shader');
  root.classList.toggle('pastel', theme === 'pastel');
  if (theme === 'pastel') {
    root.dataset.palette = palette;
    if (panel) root.dataset.panel = panel;
    else delete root.dataset.panel;
  } else {
    delete root.dataset.palette;
    delete root.dataset.panel;
  }
}

function getSnapshot(): ThemeState {
  if (!state) {
    state = THEME_LOCKED ? { ...LOCKED_STATE } : { theme: readTheme(), palette: readPalette(), panel: readPanel() };
    applyToRoot(state);
  }
  return state;
}

function setState(next: Partial<ThemeState>) {
  if (THEME_LOCKED) return; // interface verrouillée : rien ne bouge, rien n'est mémorisé
  const current = getSnapshot();
  state = { ...current, ...next };
  applyToRoot(state);
  localStorage.setItem(THEME_KEY, state.theme);
  localStorage.setItem(PALETTE_KEY, state.palette);
  if (state.panel) localStorage.setItem(PANEL_KEY, state.panel);
  else localStorage.removeItem(PANEL_KEY);
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function useTheme() {
  const { theme, palette, panel } = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const setTheme = useCallback((next: Theme) => setState({ theme: next }), []);
  // Changer de nuancier reprend le panneau par défaut de sa famille.
  const setPalette = useCallback((next: Palette) => setState({ palette: next, panel: null }), []);
  const setPanel = useCallback((next: Panel) => setState({ panel: next }), []);

  // Cycle : clair → sombre → shader → pastel → clair.
  const toggle = useCallback(() => {
    const current = getSnapshot().theme;
    const idx = THEMES.indexOf(current);
    setState({ theme: THEMES[(idx + 1) % THEMES.length] });
  }, []);

  /** Panneau effectivement affiché (choix explicite ou défaut du nuancier). */
  const effectivePanel: Panel = panel ?? defaultPanelFor(palette);

  return { theme, palette, panel, effectivePanel, toggle, setTheme, setPalette, setPanel };
}
